import { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import {
  ACTION_TOAST_KINDS,
  ActionToastKind,
  ActionToastTone,
} from './actionToastKinds';

const EXIT_DURATION_MS = 200;

const TONE_COLOR: Record<ActionToastTone, string> = {
  success: '#2e7d32',
  danger: '#c62828',
  warning: '#b26a00',
  info: '#1565c0',
};

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    minWidth: 300,
    maxWidth: 380,
    borderRadius: 10,
    animation: '$slideIn 220ms cubic-bezier(0.16, 1, 0.3, 1)',
  },
  cardExiting: {
    animation: '$slideOut 200ms ease-in forwards',
  },
  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translateX(24px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
  },
  '@keyframes slideOut': {
    from: { opacity: 1, transform: 'translateX(0)' },
    to: { opacity: 0, transform: 'translateX(24px)' },
  },
  icon: {
    display: 'flex',
    flexShrink: 0,
    marginTop: 2,
  },
  // One keyframe per tone — this is the "icon animation" itself, distinct
  // from the card's own entrance slide above.
  iconSuccess: {
    animation: '$bounceIn 480ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  '@keyframes bounceIn': {
    '0%': { transform: 'scale(0)' },
    '60%': { transform: 'scale(1.25) rotate(8deg)' },
    '100%': { transform: 'scale(1) rotate(0deg)' },
  },
  iconDanger: {
    animation: '$shake 420ms ease-in-out',
  },
  '@keyframes shake': {
    '0%, 100%': { transform: 'translateX(0)' },
    '20%': { transform: 'translateX(-4px)' },
    '40%': { transform: 'translateX(4px)' },
    '60%': { transform: 'translateX(-3px)' },
    '80%': { transform: 'translateX(3px)' },
  },
  iconWarning: {
    animation: '$pulse 380ms ease-in-out 2',
  },
  '@keyframes pulse': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.12)' },
  },
  iconInfo: {
    animation: '$fadeUp 320ms ease-out',
  },
  '@keyframes fadeUp': {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  closeButton: {
    marginTop: -4,
    marginRight: -8,
  },
}));

const ICON_ANIMATION_CLASS: Record<
  ActionToastTone,
  keyof ReturnType<typeof useStyles>
> = {
  success: 'iconSuccess',
  danger: 'iconDanger',
  warning: 'iconWarning',
  info: 'iconInfo',
};

export type ActionToastProps = {
  kind: ActionToastKind;
  title: string;
  description?: string;
  timeout?: number;
  onDismiss: () => void;
};

// A single toast card — icon animates in with a tone-specific motion
// (bounce for success, shake for danger, pulse for warning, fade-up for
// info) while the card itself slides in from the right. Exit is a short
// fade+slide before actually unmounting, so dismissal doesn't just pop.
export const ActionToast = ({
  kind,
  title,
  description,
  timeout = 5000,
  onDismiss,
}: ActionToastProps) => {
  const classes = useStyles();
  const [closing, setClosing] = useState(false);
  const { icon: Icon, tone } = ACTION_TOAST_KINDS[kind];

  const close = () => setClosing(true);

  useEffect(() => {
    const timer = setTimeout(close, timeout);
    return () => clearTimeout(timer);
  }, [timeout]);

  useEffect(() => {
    if (!closing) return undefined;
    const timer = setTimeout(onDismiss, EXIT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [closing, onDismiss]);

  return (
    <Paper
      elevation={4}
      className={`${classes.card} ${closing ? classes.cardExiting : ''}`}
    >
      <Box
        className={`${classes.icon} ${classes[ICON_ANIMATION_CLASS[tone]]}`}
        style={{ color: TONE_COLOR[tone] }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box className={classes.body}>
        <Typography variant="body2">
          <strong>{title}</strong>
        </Typography>
        {description && (
          <Typography variant="caption" color="textSecondary">
            {description}
          </Typography>
        )}
      </Box>
      <IconButton size="small" className={classes.closeButton} onClick={close}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};
